import React, { useCallback, useEffect, useMemo, type PropsWithChildren, useRef } from 'react';
import noop from 'lodash/noop';

import { graphql, useFragment } from 'react-relay';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { useIsBulkOperationsInProgress } from '@atlassian/jira-bulk-operations-common/src/controllers/use-bulk-operations/index.tsx';
import { AsyncBulkOpsCoreFull } from '@atlassian/jira-bulk-operations-full-core/src/async.tsx';
import type { BulkOperationsCoreFullProps } from '@atlassian/jira-bulk-operations-full-core/src/EntryPointContent.tsx';
import {
	useIsSelectingAll,
	useIssueTableSelectedIssues,
	useIssueTableSelectedIssuesActions,
	useIssueTableSelectAllTotalSelected,
	useExcludedIssueIds,
} from '@atlassian/jira-native-issue-table/src/controllers/selected-issues/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { BULK_OPS_SELECTED_ISSUE_LIMIT } from '@atlassian/jira-bulk-operations-common/src/common/constants.tsx';
import type { BulkOperationsWrapper$key } from '@atlassian/jira-relay/src/__generated__/BulkOperationsWrapper.graphql';
import type {
	BulkOperationsWrapper_ViewSettings$key,
	BulkOperationsWrapper_ViewSettings$data,
} from '@atlassian/jira-relay/src/__generated__/BulkOperationsWrapper_ViewSettings.graphql';

import {
	useFlatListIssueIds,
	useFlatListChildIssueIds,
} from '../../controllers/flat-list-issues/index.tsx';
import { useSelectedView } from '../../controllers/selected-view-state/index.tsx';
import { useActiveJql, useResolvedJql } from '../../services/active-jql/index.tsx';

const EMPTY_ARRAY: string[] = [];

const LoadBulkOpsCore = ({
	bulkOpsCoreEntryPointReference,
	fragmentRef,
	jiraViewFragment,
}: Pick<BulkOperationsCoreFullProps, 'bulkOpsCoreEntryPointReference'> & {
	fragmentRef: BulkOperationsWrapper$key | null;
	// TODO remove optional jiraViewFragment when cleaning up bulk_ops_select_all_beyond_experiment
	jiraViewFragment?: BulkOperationsWrapper_ViewSettings$key | null;
}) => {
	const selectedIssues = useIssueTableSelectedIssues();
	const excludedIssueIds = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useExcludedIssueIds()
		: undefined;
	const areAllIssuesSelected = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIsSelectingAll()
		: undefined;
	const { reset, insertSelectedIssueIds, selectAllIssues } = useIssueTableSelectedIssuesActions();
	const view = useSelectedView();
	const { jql } = useActiveJql();
	const filterJql = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useResolvedJql()
		: undefined;
	const previousJql = usePrevious(jql);
	const childIssueIds = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useFlatListChildIssueIds()
		: EMPTY_ARRAY;

	const data = useFragment<BulkOperationsWrapper$key>(
		graphql`
			fragment BulkOperationsWrapper on JiraIssueConnection
			@argumentDefinitions(
				isBulkOpsSelectAllEnabled: {
					type: "Boolean!"
					provider: "@atlassian/jira-relay-provider/src/bulk-ops-select-all.relayprovider"
				}
			) {
				edges {
					node {
						issueId
					}
				}
				totalIssueSearchResultCount @include(if: $isBulkOpsSelectAllEnabled)
			}
		`,
		fragmentRef,
	);

	const viewData = useFragment<BulkOperationsWrapper_ViewSettings$key>(
		graphql`
			fragment BulkOperationsWrapper_ViewSettings on JiraSpreadsheetView {
				viewSettings(
					groupBy: $groupBy
					staticViewInput: $staticViewInput
					issueSearchInput: $issueSearchInput
					settings: $settings
				) {
					isHierarchyEnabled
					canEnableHierarchy
					hideDone
					groupByConfig {
						groupByField {
							fieldId
						}
					}
				}
			}
		`,
		jiraViewFragment,
	);

	let hideDone: NonNullable<BulkOperationsWrapper_ViewSettings$data['viewSettings']>['hideDone'];
	let isHierarchyEnabled: NonNullable<
		BulkOperationsWrapper_ViewSettings$data['viewSettings']
	>['isHierarchyEnabled'];
	let canEnableHierarchy: NonNullable<
		BulkOperationsWrapper_ViewSettings$data['viewSettings']
	>['canEnableHierarchy'];

	if (expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)) {
		hideDone = viewData?.viewSettings?.hideDone;
		isHierarchyEnabled = viewData?.viewSettings?.isHierarchyEnabled;
		canEnableHierarchy = viewData?.viewSettings?.canEnableHierarchy;

		const groupedByFieldId = viewData?.viewSettings?.groupByConfig?.groupByField?.fieldId || null;
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const isInitialized = useRef(false);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const previousGroupedByFieldId = usePrevious(groupedByFieldId);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const previousHideDone = usePrevious(hideDone);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const previousIsHierarchyEnabled = usePrevious(isHierarchyEnabled);
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useEffect(() => {
			if (!isInitialized.current) {
				isInitialized.current = true;
				return;
			}

			const groupByFieldIdChanged = previousGroupedByFieldId !== groupedByFieldId;
			const hideDoneChanged = previousHideDone !== hideDone;
			const isHierarchyEnabledChanged = previousIsHierarchyEnabled !== isHierarchyEnabled;

			if (groupByFieldIdChanged || hideDoneChanged || isHierarchyEnabledChanged) {
				reset();
			}
		}, [
			reset,
			groupedByFieldId,
			previousGroupedByFieldId,
			previousHideDone,
			hideDone,
			previousIsHierarchyEnabled,
			isHierarchyEnabled,
		]);
	}

	const totalIssueSearchResultCount = data?.totalIssueSearchResultCount ?? 0;

	const selectAllTotalSelected = expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useIssueTableSelectAllTotalSelected(
				totalIssueSearchResultCount > BULK_OPS_SELECTED_ISSUE_LIMIT
					? BULK_OPS_SELECTED_ISSUE_LIMIT
					: totalIssueSearchResultCount,
			)
		: undefined;

	useEffect(() => {
		if (jql !== previousJql && previousJql !== undefined) {
			reset();
		}
	}, [jql, previousJql, reset]);

	let issuesFlatListIds = EMPTY_ARRAY;
	if (fg('jsc_m2_hierarchy_fe_changes')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		issuesFlatListIds = useFlatListIssueIds();
	}

	// we don't need to read issueIds for grouping from the query because we already have them in the flat list
	const issueIds = useMemo(() => {
		if (fg('jsc_m2_hierarchy_fe_changes')) {
			return {
				strings: issuesFlatListIds,
				numbers: issuesFlatListIds.map((issueId) => Number(issueId)),
			};
		}

		const strings = data?.edges?.map((edge) => edge?.node?.issueId).filter(Boolean) ?? [];
		return {
			strings,
			numbers: strings.map((issueId) => Number(issueId)),
		};
	}, [data?.edges, issuesFlatListIds]);

	const onSelectAll = useCallback(() => {
		selectAllIssues({
			childIds: childIssueIds,
			visibleIds: issueIds.strings,
			count: totalIssueSearchResultCount,
		});
	}, [issueIds.strings, childIssueIds, selectAllIssues, totalIssueSearchResultCount]);

	const onSelectAllOld = useCallback(() => {
		insertSelectedIssueIds(issueIds.strings);
	}, [issueIds.strings, insertSelectedIssueIds]);

	return (
		<AsyncBulkOpsCoreFull
			bulkOpsCoreEntryPointReference={bulkOpsCoreEntryPointReference}
			selectedIssueIds={selectedIssues.map((issue) => Number(issue))}
			onClearAll={reset}
			onSelectAll={
				expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false)
					? onSelectAll
					: onSelectAllOld
			}
			selectableIssueIds={issueIds.numbers}
			onClearAllVisible={noop} // What do we do with this one?
			shouldHideToolbar={view === 'detail'}
			{...(expVal('bulk_ops_select_all_beyond_experiment', 'isEnabled', false) && {
				shouldSelectAllIssues: areAllIssuesSelected,
				selectAllTotalSelected,
				activeJql: jql ?? filterJql,
				hideDone,
				isHierarchyEnabled: canEnableHierarchy && isHierarchyEnabled,
				excludedIssueIds,
				totalIssueSearchResultCount,
			})}
		/>
	);
};

export const BulkOperationsWrapper = ({
	children,
	bulkOpsCoreEntryPointReference,
	fragmentRef,
	jiraViewFragment,
}: PropsWithChildren &
	Pick<BulkOperationsCoreFullProps, 'bulkOpsCoreEntryPointReference'> & {
		fragmentRef: BulkOperationsWrapper$key | null;
		// TODO remove optional jiraViewFragment when cleaning up bulk_ops_select_all_beyond_experiment
		jiraViewFragment?: BulkOperationsWrapper_ViewSettings$key | null;
	}) => {
	const isBulkOpsInProgress = useIsBulkOperationsInProgress();

	return (
		<Flex xcss={wrapperStyles} direction="row">
			<Box xcss={[childContainerStyles, isBulkOpsInProgress && childContainerDisabledStyles]}>
				{children}
			</Box>
			<LoadBulkOpsCore
				fragmentRef={fragmentRef}
				jiraViewFragment={jiraViewFragment}
				bulkOpsCoreEntryPointReference={bulkOpsCoreEntryPointReference}
			/>
		</Flex>
	);
};

const wrapperStyles = xcss({
	height: '100%',
	overflowY: 'auto',
});

const childContainerStyles = xcss({
	flexGrow: 1,
	flexShrink: 1,
	flexBasis: 0,
	overflowY: 'auto',
});

const childContainerDisabledStyles = xcss({
	opacity: 0.5,
	pointerEvents: 'none',
});
